import React, { useEffect, useState, useMemo } from "react";
import { Drawer, Modal, Select, DatePicker, Button, Spin, Input } from "antd";
import {
  FileOutlined,
  FileImageOutlined,
  PlaySquareOutlined,
  AudioOutlined,
  DeleteOutlined,
  CheckOutlined,
  DownOutlined,
  RightOutlined,
  MessageFilled,
  SearchOutlined,
} from "@ant-design/icons";
import { Capacitor } from "@capacitor/core";
import { useIsTimeVariant } from "../../../../../helpers/appPackage";
import { useAppDispatch, useAppSelector } from "../../../../../store";
import { RootState } from "../../../../../store";
import { GetTeamMemberListAPI } from "../../../../Redux/teamMember";
import { getLockDatesAPI } from "../../../../Redux/lockDate";
import { getDecryptedUserData, getDecryptedName } from "../../../../../helpers/bycrypt";
import {
  getTimeOffTypesAPI,
  createTimeOffRequestAPI,
  updateTimeOffRequestAPI,
  getEmployeeDetailsAPI,
  getTimeOffRequestsAPI,
} from "../../timeOffApis";
import { useLocation } from "react-router-dom";
import { S3FileUploader } from "../../../../../Components/S3FileUploader";
import { svg } from "../../../../../Assets/svg";
import { getFullAwsUrl, getBusinessDateYear, getBusinessCurrentYear, getSystemStartDate, getSystemStartYear } from "../../../../../helpers/utils";
import { convertLocalDayjsToBusinessUTC, formatCalendarDateUTC, getBusinessTimeZone, formatTimeOffSlot, formatCreatedLeavesSummary, getEmploymentEndDateString, formatCalendarDateDisplay } from "../../../../../helpers/moment";
import dayjs from "dayjs";
import moment from "moment-timezone";
import TimeOffTypeBadge from "../../../../Components/TimeOffTypeBadge";
import { AlreadyAppliedTimeOffModal } from "./AlreadyAppliedTimeOffModal";

const { TextArea } = Input;

const extractAlreadyAppliedLeaves = (resData: any): any[] => {
  if (!resData) return [];
  if (Array.isArray(resData?.already_applied_leaves) && resData.already_applied_leaves.length > 0) {
    return resData.already_applied_leaves;
  }
  if (Array.isArray(resData)) {
    for (const item of resData) {
      if (Array.isArray(item?.already_applied_leaves) && item.already_applied_leaves.length > 0) {
        return item.already_applied_leaves;
      }
    }
  }
  return [];
};

interface BookTimeOffModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: (data?: any) => void;
  employeeId?: string; // Pre-selected employee if inside user profile
  employeeName?: string; // Pre-selected employee name to avoid showing Object ID
  editData?: any; // Data for modifying an existing request
  preselectedDate?: string; // Date to prefill when opening the modal directly for a specific day
  existingRequests?: any[]; // Existing time off requests to compute pending planned days
}

const isAfternoonStart = (val: number | string) => {
  return val === 2 || (typeof val === "string" && val >= "13:00");
};

const isLunchtimeEnd = (val: number | string) => {
  return val === 3 || (typeof val === "string" && val <= "13:00");
};

const mapStartToBackendTimeType = (type: number | string): number => {
  if (type === 1 || type === 2) return type;
  return typeof type === "string" && type >= "13:00" ? 2 : 1;
};

const mapEndToBackendTimeType = (type: number | string): number => {
  if (type === 3 || type === 4) return type;
  return typeof type === "string" && type <= "13:00" ? 3 : 4;
};

const getActiveSchedule = (schs: any[], date: any) => {
  if (!schs || schs.length === 0) return null;
  const targetTime = date?.toDate ? date.toDate().getTime() : (date ? new Date(date).getTime() : Date.now());
  const sorted = [...schs].sort(
    (a: any, b: any) =>
      new Date(b.effective_date || b.created_at).getTime() -
      new Date(a.effective_date || a.created_at).getTime()
  );
  const active = sorted.find(
    (s: any) =>
      new Date(s.effective_date || s.created_at).getTime() <= targetTime
  );
  return active || sorted[sorted.length - 1];
};

const calculateWorkingDays = (fromDate: moment.Moment, toDate: moment.Moment) => {
  let days = 0;
  let curr = fromDate.clone().startOf('day').add(1, 'day');
  const target = toDate.clone().startOf('day');
  while (curr.isBefore(target)) {
    if (curr.day() !== 0 && curr.day() !== 6) {
      days++;
    }
    curr.add(1, 'day');
  }
  return days;
};

export const BookTimeOffModal: React.FC<BookTimeOffModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  employeeId,
  employeeName,
  editData,
  preselectedDate,
  existingRequests,
}) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { teamMemberList } = useAppSelector((state: RootState) => state.teamMember);
  const { USER_PERMISSIONS } = useAppSelector((state: RootState) => state.auth);
  const userData = getDecryptedUserData();

  const [selectedEmployee, setSelectedEmployee] = useState<string>(
    employeeId || (editData?.user ? (editData.user._id || editData.user) : "")
  );

  const [timeOffTypes, setTimeOffTypes] = useState<any[]>([]);
  const [typesLoading, setTypesLoading] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<string>(
    editData?.type ? (editData.type._id || editData.type) : ""
  );
  const [lockDates, setLockDates] = useState<string[]>([]);

  const [startDate, setStartDate] = useState<any>(null);
  const [startTimeType, setStartTimeType] = useState<number | string>(1);

  const [endDate, setEndDate] = useState<any>(null);
  const [endTimeType, setEndTimeType] = useState<number | string>(4);

  // Sub-drawers state
  const [timeDrawerOpen, setTimeDrawerOpen] = useState<boolean>(false);
  const [drawerType, setDrawerType] = useState<"from" | "to">("from");
  const [typeDrawerOpen, setTypeDrawerOpen] = useState<boolean>(false);
  const [employeeDrawerOpen, setEmployeeDrawerOpen] = useState<boolean>(false);
  const [employeeSearch, setEmployeeSearch] = useState<string>("");

  const [reason, setReason] = useState<string>("");
  const [attachmentsUploadingCount, setAttachmentsUploadingCount] = useState(0);
  const [modalStagedFiles, setModalStagedFiles] = useState<{ _id?: string; attachment: string }[]>([]);

  const [loading, setLoading] = useState(false);
  const [employeeDetailsData, setEmployeeDetailsData] = useState<any>(null);
  const [fetchedRequests, setFetchedRequests] = useState<any[]>([]);
  const [alreadyAppliedNotice, setAlreadyAppliedNotice] = useState<{
    visible: boolean;
    leaves: any[];
    lastData?: any;
    createdCount?: number;
    createdDateRange?: string;
    employeeName?: string;
  } | null>(null);

  useEffect(() => {
    if (!visible) return;
    const targetUserId =
      selectedEmployee || editData?.user?._id || editData?.user || userData?._id;
    if (
      (!existingRequests || existingRequests.length === 0) &&
      !(location.state as any)?.existingRequests &&
      !(location.state as any)?.timeOffRequests &&
      targetUserId
    ) {
      dispatch(
        getTimeOffRequestsAPI({
          payload: {
            user: targetUserId,
            status: "pending",
            limit: 1000,
          },
          onSuccess: (resData: any) => {
            const items = Array.isArray(resData) ? resData : resData?.data || [];
            setFetchedRequests(items);
          },
          onError: () => {
            setFetchedRequests([]);
          },
        })
      );
    }
  }, [
    visible,
    selectedEmployee,
    editData?.user,
    userData?._id,
    existingRequests,
    location.state,
    dispatch,
  ]);

  const [employeePage, setEmployeePage] = useState(1);
  const [hasMoreEmployees, setHasMoreEmployees] = useState(true);
  const [errors, setErrors] = useState<any>({});
  const isTimeVariant = useIsTimeVariant();
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show Drawer if mobile view (< 768) OR if running in native app (Transform app or Time app)
  const isNative = Capacitor.isNativePlatform();
  const showDrawer = isMobile || isNative || isTimeVariant;

  useEffect(() => {
    if (visible) {
      if (employeeId) {
        setSelectedEmployee(employeeId);
      }

      setEmployeePage(1);
      dispatch(GetTeamMemberListAPI({ limit: 30, page: 1, isEmployee: true, is_active: 1, isActive: true, employee_profile_status: 'Completed', ...(employeeId && { userId: employeeId }) }, (hasMore: boolean) => {
        setHasMoreEmployees(hasMore);
      }));

      setTypesLoading(true);
      dispatch(
        getTimeOffTypesAPI({
          onSuccess: (data) => { setTimeOffTypes(data); setTypesLoading(false); },
          onError: () => { setTimeOffTypes([]); setTypesLoading(false); },
        })
      );

      dispatch(
        getLockDatesAPI({
          year: new Date().getFullYear(),
          onSuccess: (data) => {
            const tz = getBusinessTimeZone();
            const formattedDates = data
              .map((item: any) => {
                const d = item.date;
                if (!d) return null;
                return moment(d).tz(tz).format('YYYY-MM-DD');
              })
              .filter((d): d is string => !!d);
            setLockDates(formattedDates);
          },
          onError: () => setLockDates([]),
        })
      );

      if (editData) {
        if (editData.user) setSelectedEmployee(editData.user._id || editData.user);
        if (editData.type) setSelectedType(editData.type._id || editData.type);
        if (editData.starting) {
          setStartDate(
            editData.starting.date
              ? dayjs(typeof editData.starting.date === "string" ? editData.starting.date.substring(0, 10) : moment.utc(editData.starting.date).format("YYYY-MM-DD"))
              : null
          );
          setStartTimeType(editData.starting.time_type);
        }
        if (editData.ending) {
          setEndDate(
            editData.ending.date
              ? dayjs(typeof editData.ending.date === "string" ? editData.ending.date.substring(0, 10) : moment.utc(editData.ending.date).format("YYYY-MM-DD"))
              : null
          );
          setEndTimeType(editData.ending.time_type);
        }
        if (editData.reason) {
          setReason(editData.reason.note || "");
          if (editData.reason.attachments) {
            setModalStagedFiles(editData.reason.attachments);
          }
        }
      } else {
        setSelectedType("");
        if (preselectedDate) {
          setStartDate(dayjs(preselectedDate));
          setEndDate(dayjs(preselectedDate));
        } else {
          setStartDate(null);
          setEndDate(null);
        }
        setStartTimeType(1);
        setEndTimeType(4);
        setReason("");
        setModalStagedFiles([]);
        setErrors({});
      }
    }
  }, [visible, employeeId, editData, preselectedDate, dispatch, USER_PERMISSIONS]);


  const handleUploadSuccess = (key: string, fileName: string) => {
    setAttachmentsUploadingCount((prev) => Math.max(0, prev - 1));
    const url = key;
    const newFile = { attachment: url };
    setModalStagedFiles((prev) => [...prev, newFile]);
  };

  const handleRemoveFile = (index: number) => {
    setModalStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const newErrors: any = {};
    if (!selectedEmployee) newErrors.employee = "Please select an employee";
    if (!selectedType) newErrors.type = "Please select a time off type";
    if (isNonDeductibleAppointmentType(currentTypeObj) && !isAppointmentAllowedForSelected) {
      newErrors.type = "This employee is not allowed to take non-deductible Appointment time off";
    }
    if (!startDate) newErrors.startDate = "Please select a starting date";
    if (!endDate) newErrors.endDate = "Please select an ending date";

    const currentYear = getBusinessCurrentYear();
    const systemStartYear = getSystemStartYear();
    const minAllowedYear = Math.max(systemStartYear, currentYear - 1);
    const maxAllowedYear = currentYear + 1;

    if (startDate) {
      const startYear = getBusinessDateYear(startDate);
      if (startYear < minAllowedYear || startYear > maxAllowedYear) {
        newErrors.startDate = `Time off requests can only be booked between ${minAllowedYear} and ${maxAllowedYear}`;
      }
    }
    if (endDate) {
      const endYear = getBusinessDateYear(endDate);
      if (endYear < minAllowedYear || endYear > maxAllowedYear) {
        newErrors.endDate = `Time off requests can only be booked between ${minAllowedYear} and ${maxAllowedYear}`;
      }
    }

    if (isDeductible && isPreviousYear && availableAllowance <= 0) {
      newErrors.startDate = `No time off allowance available for ${selectedDateYear}`;
    }

    if (empEndDateStr && startDate) {
      const startCalStr = startDate.format("YYYY-MM-DD");
      const endCalStr = endDate ? endDate.format("YYYY-MM-DD") : startCalStr;
      if (startCalStr > empEndDateStr || endCalStr > empEndDateStr) {
        const displayEnd = moment.utc(empEndDateStr, "YYYY-MM-DD").format("DD/MM/YYYY");
        newErrors.startDate = `Cannot request time off after employment end date (${displayEnd}).`;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    const isHourlyBooking =
      canShowSlots &&
      typeof startTimeType === "string" &&
      typeof endTimeType === "string" &&
      startTimeType.includes(":") &&
      endTimeType.includes(":");

    const payload: any = {
      user: selectedEmployee,
      type: selectedType,
      leave_type: isHourlyBooking ? "hours" : "day",
      starting: {
        date: formatCalendarDateUTC(startDate),
        time_type: isHourlyBooking ? startTimeType : mapStartToBackendTimeType(startTimeType),
      },
      ending: {
        date: formatCalendarDateUTC(isHourlyBooking ? startDate : endDate),
        time_type: isHourlyBooking ? endTimeType : mapEndToBackendTimeType(endTimeType),
      },
      reason: {
        note: reason,
        attachments: modalStagedFiles,
      },
    };

    if (editData) {
      // @ts-ignore
      dispatch(
        // @ts-ignore
        updateTimeOffRequestAPI({
          id: editData._id,
          payload,
          onSuccess: (data: any) => {
            setLoading(false);
            const leaves = extractAlreadyAppliedLeaves(data);
            if (leaves && leaves.length > 0) {
              const summary = formatCreatedLeavesSummary(data);
              setAlreadyAppliedNotice({
                visible: true,
                leaves,
                lastData: data,
                createdCount: summary.createdCount || (Array.isArray(data) ? data.length : 1),
                createdDateRange: summary.createdDateRange,
                employeeName: currentEmployeeName,
              });
            } else {
              onSuccess?.(data);
              handleClose();
            }
          },
          onError: () => {
            setLoading(false);
          },
        })
      );
    } else {
      dispatch(
        createTimeOffRequestAPI({
          payload,
          onSuccess: (data: any) => {
            setLoading(false);
            const leaves = extractAlreadyAppliedLeaves(data);
            if (leaves && leaves.length > 0) {
              const summary = formatCreatedLeavesSummary(data);
              setAlreadyAppliedNotice({
                visible: true,
                leaves,
                lastData: data,
                createdCount: summary.createdCount || (Array.isArray(data) ? data.length : 1),
                createdDateRange: summary.createdDateRange,
                employeeName: currentEmployeeName,
              });
            } else {
              onSuccess?.(data);
              handleClose();
            }
          },
          onError: () => {
            setLoading(false);
          },
        })
      );
    }
  };

  const handleCloseNotice = () => {
    const lastData = alreadyAppliedNotice?.lastData;
    setAlreadyAppliedNotice(null);
    onSuccess?.(lastData);
    handleClose();
  };

  const handleClose = () => {
    setSelectedEmployee(employeeId || "");
    setSelectedType("");
    setStartDate(null);
    setStartTimeType(1);
    setEndDate(null);
    setEndTimeType(4);
    setReason("");
    setModalStagedFiles([]);
    setErrors({});
    setTimeDrawerOpen(false);
    setTypeDrawerOpen(false);
    setEmployeeDrawerOpen(false);
    setAlreadyAppliedNotice(null);
    onCancel();
  };

  const getFileNameFromUrl = (key: string) => {
    if (!key) return "";
    try {
      const parts = key.split("/");
      const fullName = parts[parts.length - 1];
      if (fullName.includes("_")) {
        return fullName.substring(fullName.indexOf("_") + 1);
      }
      return fullName;
    } catch (e) {
      return key;
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext || "")) {
      return <FileImageOutlined className="text-primary fs-5" />;
    }
    if (["mp4", "mov", "avi", "mkv"].includes(ext || "")) {
      return <PlaySquareOutlined className="text-danger fs-5" />;
    }
    if (["mp3", "wav", "m4a", "ogg"].includes(ext || "")) {
      return <AudioOutlined className="text-warning fs-5" />;
    }
    return <FileOutlined className="text-secondary fs-5" />;
  };

  const currentEmployee = teamMemberList?.find((emp: any) => emp._id === selectedEmployee) || null;

  const currentEmployeeName =
    employeeName ||
    (currentEmployee
      ? currentEmployee.name ||
      `${currentEmployee.first_name || ""} ${currentEmployee.last_name || ""}`.trim() ||
      currentEmployee?.employee_details?.employee_name
      : editData?.user
        ? getDecryptedName(editData.user) || editData.user.name || `${editData.user.first_name || ""} ${editData.user.last_name || ""}`.trim()
        : "Select Employee");

  const getInitials = (name: string) => {
    if (!name || name === "Select Employee") return "U";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const getEmployeeProfilePic = (emp: any) => {
    if (!emp) return "";
    const rawPic =
      emp?.employee_details?.basic_details?.profile_picture ||
      (typeof emp?.employee_details?.biometric_authentication?.face === "string"
        ? emp?.employee_details?.biometric_authentication?.face
        : emp?.employee_details?.biometric_authentication?.face?.photo) ||
      emp?.basic_details?.profile_picture ||
      (typeof emp?.biometric_authentication?.face === "string"
        ? emp?.biometric_authentication?.face
        : emp?.biometric_authentication?.face?.photo) ||
      emp?.profile_pic ||
      "";
    return typeof rawPic === "string" ? rawPic.trim() : "";
  };

  const EmployeeAvatar: React.FC<{
    employee: any;
    name: string;
    className?: string;
  }> = ({ employee, name, className = "drawer-avatar" }) => {
    const [imgError, setImgError] = useState(false);
    const pic = getEmployeeProfilePic(employee);

    useEffect(() => {
      setImgError(false);
    }, [pic]);

    return (
      <div className={className}>
        {pic && !imgError ? (
          <img
            src={getFullAwsUrl(pic)}
            alt={name}
            onError={() => setImgError(true)}
          />
        ) : (
          getInitials(name)
        )}
      </div>
    );
  };

  const isNonDeductibleAppointmentType = (t: any) =>
    t?.name?.trim().toLowerCase() === "appointment" &&
    t?.type?.trim().toLowerCase() === "non-deductible";

  const editTypeObj =
    editData?.type && typeof editData.type === "object" ? editData.type : null;
  const editTypeId = editTypeObj
    ? editTypeObj._id
    : typeof editData?.type === "string"
    ? editData.type
    : "";
  const editTypeName =
    editTypeObj?.name ||
    (typeof editData?.type_name === "string" ? editData.type_name : "");
  const editTypeKind = editTypeObj?.type || "";

  const isAppointmentAllowedForSelected = Boolean(
    currentEmployee?.employee_details?.basic_details?.allow_non_deductible_appointment ||
    currentEmployee?.basic_details?.allow_non_deductible_appointment ||
    editData?.user?.employee_details?.basic_details?.allow_non_deductible_appointment ||
    editData?.user?.basic_details?.allow_non_deductible_appointment
  );

  const availableTimeOffTypes = timeOffTypes.filter((type: any) => {
    // If editing existing time-off and this is the existing type, always allow it!
    if (editTypeId && type._id === editTypeId) {
      return true;
    }
    if (isNonDeductibleAppointmentType(type)) {
      return isAppointmentAllowedForSelected;
    }
    return true;
  });

  const currentTypeObj =
    timeOffTypes.find((t: any) => t._id === selectedType) ||
    (editTypeObj && (editTypeObj._id === selectedType || !selectedType)
      ? editTypeObj
      : null);

  useEffect(() => {
    if (!selectedType) return;
    const isExistingEditType = Boolean(
      editTypeId && selectedType === editTypeId
    );
    if (
      !isExistingEditType &&
      isNonDeductibleAppointmentType(currentTypeObj) &&
      !isAppointmentAllowedForSelected
    ) {
      setSelectedType("");
    }
  }, [selectedEmployee, isAppointmentAllowedForSelected, currentTypeObj, selectedType, editTypeId]);

  const payrollDetails = currentEmployee?.employee_details?.payroll_details || currentEmployee?.payroll_details;
  const duration = payrollDetails?.duration?.toLowerCase();
  const isMonthly = duration === "monthly";
  const isNonDeductible = currentTypeObj?.type === "non-deductible";
  const isDeductible = currentTypeObj?.type === "deductible";
  const canShowSlots = Boolean(isMonthly && isNonDeductible);

  const schedules = currentEmployee?.employee_details?.schedule || currentEmployee?.schedule || [];
  const slotMinutes = Number(userData?.businessDetails?.monthly_time_off_slot) || 15;

  const getSlotsForDate = (date: any): string[] => {
    if (!date) return [];
    const activeSched = getActiveSchedule(schedules, date);
    const dayOfWeekStr = date.format ? date.format("ddd") : dayjs(date).format("ddd");
    const daySched = activeSched?.days?.[dayOfWeekStr];

    const startStr = daySched?.start || "08:00";
    const endStr = daySched?.end || "17:00";

    const [startH, startM] = startStr.split(":").map(Number);
    const [endH, endM] = endStr.split(":").map(Number);
    let currentMinutes = (isNaN(startH) ? 8 : startH) * 60 + (isNaN(startM) ? 0 : startM);
    let endMinutes = (isNaN(endH) ? 17 : endH) * 60 + (isNaN(endM) ? 0 : endM);

    if (endMinutes <= currentMinutes) {
      endMinutes = currentMinutes + 8 * 60;
    }

    const slots: string[] = [];
    while (currentMinutes <= endMinutes) {
      const h = Math.floor(currentMinutes / 60);
      const m = currentMinutes % 60;
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      slots.push(`${hh}:${mm}`);
      currentMinutes += slotMinutes;
    }
    return slots;
  };

  useEffect(() => {
    if (!canShowSlots) {
      if (typeof startTimeType === "string") {
        setStartTimeType(1);
      }
      if (typeof endTimeType === "string") {
        setEndTimeType(4);
      }
    }
  }, [canShowSlots]);

  const isTimeSlot = typeof startTimeType === "string" || typeof endTimeType === "string";

  useEffect(() => {
    if (isTimeSlot && startDate) {
      if (!endDate || !endDate.isSame(startDate, "day")) {
        setEndDate(startDate);
      }
    }
  }, [isTimeSlot, startDate, endDate]);

  const currentFromOptions: { label: string; value: number | string }[] = useMemo(() => {
    const base: { label: string; value: number | string }[] = [
      { label: "Morning", value: 1 },
      { label: "Afternoon", value: 2 },
    ];
    if (!canShowSlots) return base;

    const dateToUse = startDate || dayjs();
    const slots = getSlotsForDate(dateToUse);
    return [
      ...base,
      ...slots.map((t) => ({ label: formatTimeOffSlot(t), value: t })),
    ];
  }, [canShowSlots, startDate, schedules, slotMinutes]);

  const currentToOptions: { label: string; value: number | string }[] = useMemo(() => {
    if (!canShowSlots) {
      if (startTimeType === 2) {
        return [{ label: "End of Day", value: 4 }];
      }
      return [
        { label: "Lunchtime", value: 3 },
        { label: "End of Day", value: 4 },
      ];
    }

    // canShowSlots is true
    if (typeof startTimeType === "string") {
      const dateToUse = startDate || endDate || dayjs();
      const slots = getSlotsForDate(dateToUse);
      return slots
        .filter((t) => t > startTimeType)
        .map((t) => ({ label: formatTimeOffSlot(t), value: t }));
    }

    // startTimeType is Morning (1) or Afternoon (2)
    if (startTimeType === 2) {
      return [{ label: "End of Day", value: 4 }];
    }
    return [
      { label: "Lunchtime", value: 3 },
      { label: "End of Day", value: 4 },
    ];
  }, [canShowSlots, startTimeType, startDate, endDate, schedules, slotMinutes]);

  const getAllowanceDays = () => {
    if (!startDate || !endDate) return null;

    let curr = startDate.clone().startOf('day');
    const end = endDate.clone().startOf('day');

    if (curr.isAfter(end)) return 0;

    if (!schedules || schedules.length === 0) {
      let totalHalves = 0;
      let currentHalf = isAfternoonStart(startTimeType) ? 1 : 0;
      while (curr.isBefore(end) || curr.isSame(end, 'day')) {
        const isWorkingDay = curr.day() !== 0 && curr.day() !== 6;
        if (isWorkingDay) {
          let dayHalves = 2;
          if (curr.isSame(end)) {
            dayHalves = isLunchtimeEnd(endTimeType) ? 1 : 2;
          }
          totalHalves += (dayHalves - currentHalf);
        }
        curr = curr.add(1, 'day');
        currentHalf = 0;
      }
      return totalHalves > 0 ? totalHalves * 0.5 : 0;
    }

    let totalHalves = 0;
    let currentHalf = isAfternoonStart(startTimeType) ? 1 : 0; // 1:Morning->0, 2:Afternoon->1

    while (curr.isBefore(end) || curr.isSame(end, 'day')) {
      const dayOfWeekStr = curr.format("ddd");
      const schedule = getActiveSchedule(schedules, curr);
      const hasActiveDays = schedule && schedule.days && Object.values(schedule.days).some((d: any) => (d as any)?.isActive);
      const isWorkingDay = hasActiveDays
        ? Boolean(schedule.days[dayOfWeekStr] && schedule.days[dayOfWeekStr].isActive)
        : (curr.day() !== 0 && curr.day() !== 6);

      if (isWorkingDay) {
        let dayHalves = 2;
        if (curr.isSame(end)) {
          dayHalves = isLunchtimeEnd(endTimeType) ? 1 : 2; // Lunchtime->1, EndOfDay->2
        }
        totalHalves += (dayHalves - currentHalf);
      }

      curr = curr.add(1, 'day');
      currentHalf = 0;
    }

    return totalHalves > 0 ? totalHalves * 0.5 : 0;
  };

  const formatAllowanceDays = (days: number | null) => {
    if (days === null) return null;
    if (days % 1 === 0.5) {
      const intPart = Math.floor(days);
      return intPart === 0 ? "½" : `${intPart}½`;
    }
    return days.toString();
  };

  const getTimeOffDurationHHMM = () => {
    if (typeof startTimeType !== "string" || typeof endTimeType !== "string") return null;
    const [startH, startM] = startTimeType.split(":").map(Number);
    const [endH, endM] = endTimeType.split(":").map(Number);
    if (isNaN(startH) || isNaN(startM) || isNaN(endH) || isNaN(endM)) return null;

    if (!startDate || !endDate || startDate.isSame(endDate, "day")) {
      const targetDate = startDate || endDate;
      if (targetDate) {
        const startDayOfWeek = targetDate.format("ddd");
        const singleSched = getActiveSchedule(schedules, targetDate);
        const hasSingleActive = singleSched && singleSched.days && Object.values(singleSched.days).some((d: any) => (d as any)?.isActive);
        const isSingleWorkingDay = hasSingleActive
          ? Boolean(singleSched.days[startDayOfWeek] && singleSched.days[startDayOfWeek].isActive)
          : (targetDate.day() !== 0 && targetDate.day() !== 6);

        if (!isSingleWorkingDay) {
          return "00:00";
        }
      }

      let diffMins = (endH * 60 + endM) - (startH * 60 + startM);
      if (diffMins < 0) diffMins = 0;
      const h = Math.floor(diffMins / 60);
      const m = diffMins % 60;
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }

    let curr = startDate.clone().startOf("day");
    const end = endDate.clone().startOf("day");
    let totalMinutes = 0;

    while (curr.isBefore(end) || curr.isSame(end, "day")) {
      const dayOfWeekStr = curr.format("ddd");
      const sched = getActiveSchedule(schedules, curr);
      const hasActiveDays = sched && sched.days && Object.values(sched.days).some((d: any) => (d as any)?.isActive);
      const isWorkingDay = hasActiveDays
        ? Boolean(sched.days[dayOfWeekStr] && sched.days[dayOfWeekStr].isActive)
        : (curr.day() !== 0 && curr.day() !== 6);

      if (isWorkingDay) {
        const daySched = sched?.days?.[dayOfWeekStr];
        const [dayStartH, dayStartM] = (daySched?.start || "08:00").split(":").map(Number);
        const [dayEndH, dayEndM] = (daySched?.end || "17:00").split(":").map(Number);

        const currentDayStartMins = curr.isSame(startDate, "day") ? (startH * 60 + startM) : (dayStartH * 60 + dayStartM);
        const currentDayEndMins = curr.isSame(end, "day") ? (endH * 60 + endM) : (dayEndH * 60 + dayEndM);

        if (currentDayEndMins > currentDayStartMins) {
          totalMinutes += (currentDayEndMins - currentDayStartMins);
        }
      }
      curr = curr.add(1, "day");
    }

    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const slotDurationHHMM = getTimeOffDurationHHMM();

  const [yearAllowance, setYearAllowance] = useState<any>(null);
  const selectedDateYear = startDate ? getBusinessDateYear(startDate) : getBusinessCurrentYear();
  const currentYear = getBusinessCurrentYear();
  const isFutureYear = selectedDateYear > currentYear;
  const isPreviousYear = selectedDateYear < currentYear;

  useEffect(() => {
    const targetUserId = selectedEmployee || editData?.user?._id || editData?.user;
    if (targetUserId && selectedDateYear) {
      dispatch(
        getEmployeeDetailsAPI({
          id: targetUserId,
          year: selectedDateYear,
          onSuccess: (data: any) => {
            setEmployeeDetailsData(data);
            if (data?.allowance) {
              setYearAllowance(data.allowance);
            } else {
              setYearAllowance(null);
            }
            setErrors((prev: any) => {
              if (prev?.startDate && prev.startDate.includes("allowance")) {
                const { startDate, ...rest } = prev;
                return rest;
              }
              return prev;
            });
          },
          onError: () => {
            setYearAllowance(null);
          },
        })
      );
    } else {
      setYearAllowance(null);
    }
  }, [selectedEmployee, editData?.user, selectedDateYear, dispatch]);

  const getPlannedDaysUsed = () => {
    const targetUserId =
      selectedEmployee ||
      editData?.user?._id ||
      editData?.user ||
      userData?._id;
    if (!targetUserId || !selectedDateYear) return 0;

    const historyList =
      (existingRequests && existingRequests.length > 0)
        ? existingRequests
        : ((location.state as any)?.existingRequests ||
           (location.state as any)?.timeOffRequests ||
           fetchedRequests ||
           []);

    if (!Array.isArray(historyList) || historyList.length === 0) return 0;

    let totalPlanned = 0;
    for (const req of historyList) {
      if (!req || req.status !== "pending") continue;
      if (editData?._id && String(req._id || req.id) === String(editData._id || editData.id)) continue;
      const reqUserId = (req.user?._id || req.user)?.toString();
      if (reqUserId && targetUserId && reqUserId !== targetUserId.toString()) continue;

      const reqYear = req.year
        ? Number(req.year)
        : req.starting?.date
        ? moment.utc(req.starting.date).year()
        : null;
      if (reqYear !== selectedDateYear) continue;

      const isReqDeductible =
        req.type?.type?.toLowerCase() === "deductible" ||
        req.type?.is_deductible === true ||
        timeOffTypes.find((t: any) => String(t._id) === String(req.type?._id || req.type))?.type?.toLowerCase() === "deductible";
      if (!isReqDeductible) continue;

      if (req.leave_type === "hours") continue;

      if (req.starting?.date && req.ending?.date) {
        const startDay = dayjs(typeof req.starting.date === "string" ? req.starting.date.substring(0, 10) : moment.utc(req.starting.date).format("YYYY-MM-DD"));
        const endDay = dayjs(typeof req.ending.date === "string" ? req.ending.date.substring(0, 10) : moment.utc(req.ending.date).format("YYYY-MM-DD"));
        const st = req.starting?.time_type;
        const et = req.ending?.time_type;

        if (startDay.isSame(endDay, "day")) {
          const dayOfWeekStr = startDay.format("ddd");
          const activeSched = getActiveSchedule(schedules, startDay);
          const hasActiveDays =
            activeSched &&
            activeSched.days &&
            Object.values(activeSched.days).some((d: any) => (d as any)?.isActive);
          const isWorkingDay = hasActiveDays
            ? Boolean(activeSched.days[dayOfWeekStr] && activeSched.days[dayOfWeekStr].isActive)
            : (startDay.day() !== 0 && startDay.day() !== 6);

          if (!isWorkingDay) continue;

          if (st === 1 && et === 4) {
            totalPlanned += 1;
          } else if ((st === 1 && et === 3) || (st === 2 && et === 4)) {
            totalPlanned += 0.5;
          } else {
            totalPlanned += 1;
          }
        } else {
          let curr = startDay.clone();
          let halves = 0;
          let firstHalf = isAfternoonStart(st) ? 1 : 0;
          while (curr.isBefore(endDay) || curr.isSame(endDay, "day")) {
            const dayOfWeekStr = curr.format("ddd");
            const schedule = getActiveSchedule(schedules, curr);
            const hasActiveDays =
              schedule &&
              schedule.days &&
              Object.values(schedule.days).some((d: any) => (d as any)?.isActive);
            const isWorkingDay = hasActiveDays
              ? Boolean(schedule.days[dayOfWeekStr] && schedule.days[dayOfWeekStr].isActive)
              : (curr.day() !== 0 && curr.day() !== 6);
            if (isWorkingDay) {
              let dayHalves = 2;
              if (curr.isSame(endDay)) {
                dayHalves = isLunchtimeEnd(et) ? 1 : 2;
              }
              halves += (dayHalves - firstHalf);
            }
            curr = curr.add(1, "day");
            firstHalf = 0;
          }
          totalPlanned += halves * 0.5;
        }
      }
    }
    return totalPlanned;
  };

  const getAvailableAllowance = () => {
    let allowanceData = yearAllowance;
    if (!allowanceData && !isPreviousYear) {
      allowanceData =
        currentEmployee?.allowance ||
        currentEmployee?.employee_details?.allowance;
    }
    if (!allowanceData) return 0;
    const carryForward = Number(allowanceData?.carry_forward) || 0;
    let paidLeave = Number(
      allowanceData?.paid_leave ??
      allowanceData?.paid_pto ??
      allowanceData?.pto_paid ??
      0
    );
    const pto = Number(allowanceData?.pto) || 0;

    if (selectedDateYear > currentYear && paidLeave === 0) {
      const currentYearAllowance =
        currentEmployee?.allowance ||
        currentEmployee?.employee_details?.allowance;
      if (currentYearAllowance && typeof currentYearAllowance.paid_leave === "number") {
        paidLeave = currentYearAllowance.paid_leave;
      }
    }

    const plannedUsed = getPlannedDaysUsed();
    return Math.max(0, carryForward + paidLeave - pto - plannedUsed);
  };

  const allowanceDays = getAllowanceDays();
  const availableAllowance = getAvailableAllowance();

  const rawEmpEndDate =
    employeeDetailsData?.basic_details?.employment_end_date ||
    currentEmployee?.employee_details?.basic_details?.employment_end_date ||
    currentEmployee?.basic_details?.employment_end_date ||
    (selectedEmployee === userData?._id
      ? (userData?.employment_end_date ||
         userData?.employee_details?.basic_details?.employment_end_date ||
         userData?.basic_details?.employment_end_date)
      : null);

  const empEndDateStr = getEmploymentEndDateString(rawEmpEndDate);

  const disabledStartDate = (current: dayjs.Dayjs) => {
    if (!current) return false;
    const currentYear = getBusinessCurrentYear();
    const systemStartYear = getSystemStartYear();
    const minAllowedYear = Math.max(systemStartYear, currentYear - 1);
    const maxAllowedYear = currentYear + 1;
    const minAllowedDate = dayjs(`${minAllowedYear}-01-01`).startOf('day');
    const maxAllowedDate = dayjs(`${maxAllowedYear}-12-31`).endOf('day');

    if (current < minAllowedDate || current > maxAllowedDate) return true;
    const formattedCurrent = current.format('YYYY-MM-DD');

    if (empEndDateStr && formattedCurrent > empEndDateStr) return true;

    const isPrivileged =
      userData?.role?.key === "admin" ||
      userData?.role?.key === "sub-admin" ||
      userData?.role?.key === "hr" ||
      userData?.role?.key === "hr-manager" ||
      Boolean(userData?.is_time_off);

    if (!isPrivileged && lockDates.includes(formattedCurrent)) return true;

    if (userData?.isEmployee) {
      const tz = getBusinessTimeZone();
      const businessToday = moment().tz(tz).startOf('day');
      const businessTodayFormatted = businessToday.format('YYYY-MM-DD');
      if (formattedCurrent < businessTodayFormatted) return true;

      const minimumNotice = userData?.businessDetails?.minimumTimeOff ?? 3;
      if (!isPrivileged && minimumNotice > 0) {
        const noticeDays = calculateWorkingDays(businessToday, moment(formattedCurrent, 'YYYY-MM-DD'));
        if (noticeDays < minimumNotice) return true;
      }
    }
    return false;
  };

  const disabledEndDate = (current: dayjs.Dayjs) => {
    if (!current) return false;
    const currentYear = getBusinessCurrentYear();
    const systemStartYear = getSystemStartYear();
    const minAllowedYear = Math.max(systemStartYear, currentYear - 1);
    const maxAllowedYear = currentYear + 1;
    const minAllowedDate = dayjs(`${minAllowedYear}-01-01`).startOf('day');
    const maxAllowedDate = dayjs(`${maxAllowedYear}-12-31`).endOf('day');

    if (current < minAllowedDate || current > maxAllowedDate) return true;
    const formattedCurrent = current.format('YYYY-MM-DD');

    if (empEndDateStr && formattedCurrent > empEndDateStr) return true;

    const isPrivileged =
      userData?.role?.key === "admin" ||
      userData?.role?.key === "sub-admin" ||
      userData?.role?.key === "hr" ||
      userData?.role?.key === "hr-manager" ||
      Boolean(userData?.is_time_off);

    if (!isPrivileged && lockDates.includes(formattedCurrent)) return true;

    let isPastDate = false;
    let isMinimumNoticeViolated = false;
    if (userData?.isEmployee) {
      const tz = getBusinessTimeZone();
      const businessToday = moment().tz(tz).startOf('day');
      const businessTodayFormatted = businessToday.format('YYYY-MM-DD');
      isPastDate = formattedCurrent < businessTodayFormatted;

      const minimumNotice = userData?.businessDetails?.minimumTimeOff ?? 3;
      if (!isPrivileged && minimumNotice > 0) {
        const noticeDays = calculateWorkingDays(businessToday, moment(formattedCurrent, 'YYYY-MM-DD'));
        if (noticeDays < minimumNotice) {
          isMinimumNoticeViolated = true;
        }
      }
    }
    const isBeforeStartDate = startDate && current < startDate.startOf('day');
    return !!(isPastDate || isBeforeStartDate || isMinimumNoticeViolated);
  };

  const filteredTeamMembers = teamMemberList?.filter((emp: any) => {
    if (!employeeSearch.trim()) return true;
    const name = (emp.name || `${emp.first_name || ""} ${emp.last_name || ""}`).toLowerCase();
    return name.includes(employeeSearch.toLowerCase());
  }) || [];

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { target } = e as any;
    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
      hasMoreEmployees
    ) {
      const nextPage = employeePage + 1;
      setEmployeePage(nextPage);
      dispatch(
        GetTeamMemberListAPI(
          {
            limit: 30,
            page: nextPage,
            isEmployee: true,
            is_active: 1,
            isActive: true,
            employee_profile_status: "Completed",
            ...(employeeId && { userId: employeeId }),
          },
          (hasMore: boolean) => {
            setHasMoreEmployees(hasMore);
          }
        )
      );
    }
  };

  const employeeOptions =
    teamMemberList?.map((emp: any) => {
      let isDisabled = false;
      const start_date =
        emp?.employee_details?.basic_details?.employment_start_date ||
        emp?.basic_details?.employment_start_date;
      if (start_date) {
        isDisabled = dayjs().isBefore(dayjs(start_date).startOf("day"), "day");
      }
      const end_date =
        emp?.employee_details?.basic_details?.employment_end_date ||
        emp?.basic_details?.employment_end_date;
      if (end_date) {
        const empEndStr = getEmploymentEndDateString(end_date);
        if (empEndStr && dayjs().format("YYYY-MM-DD") > empEndStr) {
          isDisabled = true;
        }
      }
      return {
        label: emp.name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
        value: emp._id,
        disabled: isDisabled,
      };
    }) || [];

  if (
    employeeId &&
    employeeName &&
    !employeeOptions.find((emp: any) => emp.value === employeeId)
  ) {
    employeeOptions.push({ label: employeeName, value: employeeId });
  }

  const editUserObj =
    editData?.user && typeof editData.user === "object" ? editData.user : null;
  const editUserId = editUserObj
    ? editUserObj._id
    : typeof editData?.user === "string"
    ? editData.user
    : "";

  if (
    editUserId &&
    !employeeOptions.find((emp: any) => emp.value === editUserId)
  ) {
    const editUserName =
      getDecryptedName(editUserObj) ||
      editUserObj?.name ||
      `${editUserObj?.first_name || ""} ${editUserObj?.last_name || ""}`.trim();
    if (editUserName) {
      employeeOptions.push({
        label: editUserName,
        value: editUserId,
      });
    }
  }

  const typeOptions = availableTimeOffTypes.map((type: any) => ({
    label: (
      <div className="d-flex align-items-center gap-2">
        <span>{type.name}</span>
        {type.type && <TimeOffTypeBadge type={type.type} />}
      </div>
    ),
    value: type._id,
  }));

  if (editTypeId && !typeOptions.find((t: any) => t.value === editTypeId)) {
    const matchedInAllTypes = timeOffTypes.find((t: any) => t._id === editTypeId);
    const nameToDisplay = editTypeName || matchedInAllTypes?.name;
    const kindToDisplay = editTypeKind || matchedInAllTypes?.type;

    if (nameToDisplay) {
      typeOptions.push({
        label: (
          <div className="d-flex align-items-center gap-2">
            <span>{nameToDisplay}</span>
            {kindToDisplay && <TimeOffTypeBadge type={kindToDisplay} />}
          </div>
        ),
        value: editTypeId,
      });
    }
  }

  const hasSelectedTypeOption = typeOptions.some((t: any) => t.value === selectedType);
  const hasSelectedEmployeeOption = employeeOptions.some((emp: any) => emp.value === selectedEmployee);

  return (
    <>
      {/* -------------------- DESKTOP WEB MODAL VIEW -------------------- */}
      {!showDrawer ? (
        <Modal
          open={visible && !alreadyAppliedNotice?.visible}
          onCancel={handleClose}
          footer={null}
          width={700}
          closeIcon={svg?.closeIcon}
          centered
          className="commonModalClass createClientDistributors book-time-off-modal"
        >
          <div className="modal-content border-0 p-0 pt-0 page-form">
            <div className="modal-header border-0 pb-1 mb-4 mt-2">
              <h1 className="modal-title fs-4 fw-bolder mx-auto text-dark" id="exampleModalLabel">
                {editData ? "Edit time off" : "Book time off"}
              </h1>
            </div>

            <div className="modal-body pb-0">
              <div className="row">
                {employeeId ? (
                  <div className="col-md-6">
                    <div className="form-item mb-3">
                      <label className="custom-label mb-1 ff">Who for</label>
                      <Input
                        size="large"
                        className="w-100"
                        value={employeeName || currentEmployeeName || ""}
                        disabled
                      />
                    </div>
                  </div>
                ) : (
                  <div className="col-md-6">
                    <div className="form-item mb-3">
                      <label className="custom-label mb-1 ff">Who for</label>
                      <Select
                        showSearch
                        allowClear
                        placeholder="Select Employee"
                        size="large"
                        className="w-100"
                        value={hasSelectedEmployeeOption ? selectedEmployee : undefined}
                        onChange={(val) => setSelectedEmployee(val || "")}
                        options={employeeOptions}
                        disabled={!!employeeId}
                        suffixIcon={svg?.selectDownArrow}
                        filterOption={(input, option) =>
                          String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        onPopupScroll={handlePopupScroll}
                        popupClassName="user-dropdown full-text-select-dropdown"
                        labelRender={(props) => {
                          const val = props.value;
                          if (
                            typeof val === "string" &&
                            /^[0-9a-fA-F]{24}$/.test(val) &&
                            (props.label === val || !props.label)
                          ) {
                            return <Spin size="small" />;
                          }
                          return props.label;
                        }}
                      />
                      {errors.employee && (
                        <div className="text-danger mt-1 form-item-error">
                          {errors.employee}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="col-md-6">
                  <div className="form-item mb-3">
                    <label className="custom-label mb-1 ff">Type</label>
                    <Select
                      showSearch
                      allowClear
                      placeholder={typesLoading ? "Loading..." : "Select Type"}
                      size="large"
                      className="w-100"
                      value={hasSelectedTypeOption ? selectedType : undefined}
                      onChange={(val) => setSelectedType(val || "")}
                      suffixIcon={svg?.selectDownArrow}
                      loading={typesLoading}
                      options={typeOptions}
                      popupClassName="user-dropdown full-text-select-dropdown"
                      labelRender={(props) => {
                        const val = props.value;
                        if (
                          typeof val === "string" &&
                          /^[0-9a-fA-F]{24}$/.test(val) &&
                          (props.label === val || !props.label)
                        ) {
                          return typesLoading ? <Spin size="small" /> : null;
                        }
                        return props.label;
                      }}
                    />
                    {errors.type && (
                      <div className="text-danger mt-1 form-item-error">
                        {errors.type}
                      </div>
                    )}
                  </div>
                </div>

                <div className="w-100"></div>

                <div className="col-md-6">
                  <div className="form-item mb-3">
                    <label className="custom-label mb-1 ff">Starting</label>
                    <div className="d-flex gap-2">
                      <DatePicker
                        className="form-control form-control-lg flex-shrink-0 datepicker-input-fixed"
                        value={startDate}
                        onChange={(date) => {
                          setStartDate(date);
                          if (isTimeSlot) {
                            setEndDate(date);
                          } else if (endDate && date && endDate.isBefore(date, "day")) {
                            setEndDate(null);
                          }
                          setErrors((prev: any) => ({
                            ...prev,
                            startDate: undefined,
                            endDate: isTimeSlot ? undefined : prev.endDate,
                          }));
                          const targetEndDate = isTimeSlot ? date : endDate;
                          if (date && targetEndDate && date.isSame(targetEndDate, "day")) {
                            if (typeof startTimeType === "string") {
                              if (typeof endTimeType !== "string" || endTimeType <= startTimeType) {
                                const endSlots = getSlotsForDate(targetEndDate);
                                const nextSlot =
                                  endSlots.find((t) => t > startTimeType) || endSlots[endSlots.length - 1];
                                setEndTimeType(nextSlot);
                              }
                            } else if (isAfternoonStart(startTimeType) && isLunchtimeEnd(endTimeType)) {
                              setEndTimeType(4);
                            }
                          }
                        }}
                        placeholder="Select Date"
                        format="DD-MM-YYYY"
                        disabledDate={disabledStartDate}
                        suffixIcon={svg?.calenderIcon}
                      />
                      <Select
                        size="large"
                        className="w-100 flex-grow-1"
                        value={startTimeType}
                        suffixIcon={svg?.selectDownArrow}
                        onChange={(val) => {
                          setStartTimeType(val);
                          if (typeof val === "string") {
                            if (startDate) {
                              setEndDate(startDate);
                            } else if (endDate) {
                              setStartDate(endDate);
                            }
                            const effectiveDate = startDate || endDate || dayjs();
                            const endSlots = getSlotsForDate(effectiveDate);
                            if (typeof endTimeType !== "string" || endTimeType <= val) {
                              const nextSlot =
                                endSlots.find((t) => t > val) || endSlots[endSlots.length - 1];
                              setEndTimeType(nextSlot);
                            }
                          } else {
                            if (typeof endTimeType === "string") {
                              setEndTimeType(4);
                            } else if (
                              isAfternoonStart(val) &&
                              isLunchtimeEnd(endTimeType) &&
                              (!startDate || !endDate || startDate.isSame(endDate, "day"))
                            ) {
                              setEndTimeType(4);
                            }
                          }
                        }}
                        options={currentFromOptions}
                      />
                    </div>
                    {errors.startDate && (
                      <div className="text-danger mt-1 form-item-error">
                        {errors.startDate}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-item mb-3">
                    <label className="custom-label mb-1 ff">Ending</label>
                    <div className="d-flex gap-2">
                      <DatePicker
                        className={`form-control form-control-lg flex-shrink-0 ${isTimeSlot && startDate ? "datepicker-input-readonly" : "datepicker-input-fixed"}`}
                        value={endDate}
                        defaultPickerValue={endDate || startDate || undefined}
                        onChange={(date) => {
                          if (isTimeSlot && startDate) {
                            setEndDate(startDate);
                            return;
                          }
                          setEndDate(date);
                          setErrors((prev: any) => ({
                            ...prev,
                            endDate: undefined,
                            startDate: prev?.startDate?.includes("Insufficient") ? undefined : prev?.startDate,
                          }));
                          if (date && startDate && date.isSame(startDate, "day")) {
                            if (typeof startTimeType === "string") {
                              if (typeof endTimeType !== "string" || endTimeType <= startTimeType) {
                                const endSlots = getSlotsForDate(date);
                                const nextSlot =
                                  endSlots.find((t) => t > startTimeType) || endSlots[endSlots.length - 1];
                                setEndTimeType(nextSlot);
                              }
                            } else if (isAfternoonStart(startTimeType) && isLunchtimeEnd(endTimeType)) {
                              setEndTimeType(4);
                            }
                          }
                        }}
                        placeholder="Select Date"
                        format="DD-MM-YYYY"
                        disabledDate={(current) => {
                          if (isTimeSlot && startDate) {
                            return !current.isSame(startDate, "day");
                          }
                          return disabledEndDate(current);
                        }}
                        suffixIcon={svg?.calenderIcon}
                      />
                      <Select
                        size="large"
                        className="w-100 flex-grow-1"
                        value={endTimeType}
                        suffixIcon={svg?.selectDownArrow}
                        onChange={(val) => {
                          setEndTimeType(val);
                          if (typeof val === "string") {
                            if (startDate) {
                              setEndDate(startDate);
                            } else if (endDate) {
                              setStartDate(endDate);
                            }
                          }
                        }}
                        options={currentToOptions}
                      />
                    </div>
                    {errors.endDate && (
                      <div className="text-danger mt-1 form-item-error">
                        {errors.endDate}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-12">
                  <div className="form-item mb-3">
                    <label className="custom-label mb-1 ff">Reason (optional)</label>
                    <TextArea
                      rows={3}
                      placeholder="Reason for time off..."
                      className="form-control form-control-lg"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-md-12 mt-2">
                  <div className="form-item mb-0">
                    <label className="custom-label mb-2 ff">Upload Attachments</label>
                    <S3FileUploader
                      folder="time_off_request_attachments"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp,.svg,.mp4,.mov,.avi,.mkv,.zip,.rar"
                      multiple={true}
                      maxSize={500 * 1024 * 1024}
                      onUploadStart={() => setAttachmentsUploadingCount((prev) => prev + 1)}
                      onSuccess={handleUploadSuccess}
                      onError={() => setAttachmentsUploadingCount((prev) => Math.max(0, prev - 1))}
                      className="bg-white rounded-3 py-0 uploader-drop-box"
                    >
                      {attachmentsUploadingCount > 0 ? (
                        <div className="py-2 text-center">
                          <Spin tip="Uploading Attachment..." />
                        </div>
                      ) : (
                        <>
                          <p className="ant-upload-drag-icon text-center mb-2 d-flex justify-content-center">
                            <span className="svg-48">{svg?.importIcon || "📄"}</span>
                          </p>
                          <p className="ant-upload-text fw-semibold text-center fs-6 text-secondary">
                            Drag & Drop file to upload /{" "}
                            <span className="text-primary text-decoration-underline cursor-pointer">
                              CLICK HERE
                            </span>
                          </p>
                        </>
                      )}
                    </S3FileUploader>

                    {modalStagedFiles.length > 0 && (
                      <div className="d-flex flex-column gap-2 mt-3 staged-files-container">
                        <div className="fw-semibold fs-7 text-secondary">
                          Uploaded Files ({modalStagedFiles.length}):
                        </div>
                        {modalStagedFiles.map((file, idx) => (
                          <div
                            key={idx}
                            className="border px-3 py-2 rounded-2 d-flex align-items-center justify-content-between bg-light gap-2"
                          >
                            <div className="d-flex align-items-center gap-2 text-truncate flex-grow-1">
                              <div className="flex-shrink-0">
                                {getFileIcon(getFileNameFromUrl(file.attachment))}
                              </div>
                              <div className="fw-semibold text-dark text-break fs-7 flex-grow-1">
                                {getFileNameFromUrl(file.attachment)}
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-1 flex-shrink-0">
                              <Button
                                type="text"
                                danger
                                icon={svg?.deleteBtn || <DeleteOutlined />}
                                className="border-0 svg-32 p-0 d-flex align-items-center justify-content-center"
                                onClick={() => handleRemoveFile(idx)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 mt-4 d-flex flex-column align-items-center justify-content-center">
                  {canShowSlots &&
                    typeof startTimeType === "string" &&
                    typeof endTimeType === "string" &&
                    slotDurationHHMM ? (
                    <div className="text-center allowance-text">
                      <span>
                        Takes{" "}
                        <span className="text-danger fw-semibold px-1">{slotDurationHHMM}</span>{" "}
                        {slotDurationHHMM === "01:00" ? "hour" : "hours"}
                      </span>
                    </div>
                  ) : isDeductible && allowanceDays !== null && currentEmployee ? (
                    <div className="text-center allowance-text">
                      {isPreviousYear && availableAllowance <= 0 ? (
                        <span className="text-danger fw-semibold">
                          No time off allowance available for {selectedDateYear} (0 Available)
                        </span>
                      ) : (
                        <span>
                          Takes{" "}
                          <span className="text-danger fw-semibold px-1">
                            {formatAllowanceDays(allowanceDays)}
                          </span>{" "}
                          {allowanceDays === 1 || allowanceDays === 0.5 ? "day" : "days"} from{" "}
                          {isFutureYear ? `${selectedDateYear} ` : ""}allowance (
                          {formatAllowanceDays(availableAllowance)} Available)
                        </span>
                      )}
                    </div>
                  ) : null}

                  {isDeductible &&
                    allowanceDays !== null &&
                    currentEmployee &&
                    allowanceDays > availableAllowance && (
                      <div className="mt-3 p-3 w-100 allowance-warning-box">
                        <div className="fw-bold mb-1 allowance-warning-title">
                          Time Off Balance Warning
                        </div>
                        <div className="allowance-warning-content">
                          {employeeId ? (
                            <>
                              You have{" "}
                              <strong>
                                {formatAllowanceDays(availableAllowance)}{" "}
                                {availableAllowance === 1 || availableAllowance === 0.5 ? "day" : "days"}
                              </strong>{" "}
                              of {isFutureYear ? `${selectedDateYear} ` : ""}time off remaining, but you are requesting{" "}
                              <strong>
                                {formatAllowanceDays(allowanceDays)}{" "}
                                {allowanceDays === 1 || allowanceDays === 0.5 ? "day" : "days"}
                              </strong>
                              . The first {formatAllowanceDays(availableAllowance)}{" "}
                              {availableAllowance === 1 || availableAllowance === 0.5 ? "day" : "days"}{" "}
                              will be approved, and the remaining{" "}
                              {formatAllowanceDays(allowanceDays - availableAllowance)}{" "}
                              {allowanceDays - availableAllowance === 1 ||
                                allowanceDays - availableAllowance === 0.5
                                ? "day"
                                : "days"}{" "}
                              will be rejected.
                            </>
                          ) : (
                            <>
                              This employee has only{" "}
                              <strong>
                                {formatAllowanceDays(availableAllowance)}{" "}
                                {availableAllowance === 1 || availableAllowance === 0.5 ? "day" : "days"}
                              </strong>{" "}
                              of {isFutureYear ? `${selectedDateYear} ` : ""}time off remaining but has requested{" "}
                              <strong>
                                {formatAllowanceDays(allowanceDays)}{" "}
                                {allowanceDays === 1 || allowanceDays === 0.5 ? "day" : "days"}
                              </strong>
                              . If you approve this request,{" "}
                              {formatAllowanceDays(availableAllowance)}{" "}
                              {availableAllowance === 1 || allowanceDays === 0.5 ? "day" : "days"}{" "}
                              will be approved and the remaining{" "}
                              {formatAllowanceDays(allowanceDays - availableAllowance)}{" "}
                              {allowanceDays - availableAllowance === 1 ||
                                allowanceDays - availableAllowance === 0.5
                                ? "day"
                                : "days"}{" "}
                              will be rejected.
                            </>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>

              <div className="d-flex justify-content-center gap-3 mt-4 pt-2 mb-4 pb-2">
                <Button
                  className="fw-bold rounded-1 px-4 px-md-5 btn-close-modal"
                  size="large"
                  onClick={handleClose}
                >
                  Close
                </Button>
                <Button
                  type="primary"
                  className="fw-bold rounded-1 px-4 px-md-5 border-0 btn-submit-modal"
                  size="large"
                  loading={loading}
                  onClick={handleSubmit}
                >
                  {editData ? "UPDATE" : "CREATE"}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      ) : (
        /* -------------------- MOBILE & APP DRAWER VIEW -------------------- */
        <>
          {/* Main Drawer replacing Modal */}
          <Drawer
            placement="bottom"
            open={visible && !alreadyAppliedNotice?.visible}
            onClose={handleClose}
            height="94vh"
            zIndex={1100}
            closable={false}
            className="book-time-off-main-drawer"
            styles={{
              body: {
                padding: 0,
                backgroundColor: "#f4f6f8",
                display: "flex",
                flexDirection: "column",
              },
              wrapper: {
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                overflow: "hidden",
              },
              content: {
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                overflow: "hidden",
              },
            }}
          >
            {/* Header matching Image 2 */}
            <div
              className="d-flex align-items-center justify-content-between py-3 px-3 bg-white border-bottom flex-shrink-0 position-relative drawer-header-bar"
            >
              <button
                type="button"
                className="btn p-0 border-0 bg-transparent d-flex align-items-center justify-content-center drawer-close-btn"
                onClick={handleClose}
                aria-label="Close"
              >
                <DownOutlined className="fs-5 fw-bold text-dark" />
              </button>

              <span
                className="fw-bold position-absolute start-0 end-0 text-center drawer-title"
              >
                {editData ? "Edit time off" : "Book time off"}
              </span>

              <div className="drawer-header-spacer" />
            </div>

            {/* Body content */}
            <div
              className="p-3 d-flex flex-column drawer-body-scroll"
            >
              <div className="mx-auto flex-fill w-100 book-time-off-container">
                {/* Card 1: Employee */}
                <div
                  className={`bg-white rounded-3 p-3 mb-3 d-flex align-items-center justify-content-between shadow-sm drawer-card ${!employeeId ? "cursor-pointer" : ""}`}
                  onClick={() => {
                    if (!employeeId) setEmployeeDrawerOpen(true);
                  }}
                >
                  <div className="d-flex align-items-center gap-2 gap-md-3">
                    <EmployeeAvatar
                      employee={currentEmployee || editData?.user}
                      name={currentEmployeeName}
                      className="drawer-avatar"
                    />
                    <div>
                      <div className="fw-semibold drawer-user-name">
                        {currentEmployeeName}
                      </div>
                    </div>
                  </div>
                  {!employeeId && <RightOutlined className="text-secondary drawer-arrow-icon" />}
                </div>
                {errors.employee && (
                  <div className="text-danger mt-n2 mb-2 px-1 form-item-error">
                    {errors.employee}
                  </div>
                )}

                {/* Card 2: Time Off Type */}
                <div
                  className="bg-white rounded-3 p-3 mb-3 d-flex align-items-center justify-content-between shadow-sm drawer-card-interactive"
                  onClick={() => setTypeDrawerOpen(true)}
                >
                  <div className="d-flex align-items-center gap-2 gap-md-3">
                    <div
                      className="drawer-type-badge-color"
                      style={{
                        backgroundColor: currentTypeObj?.color || (currentTypeObj?.type === "unpaid" ? "#16a34a" : "#10b981"),
                      }}
                    />
                    <span
                      className={`fw-medium drawer-user-name ${currentTypeObj ? "text-dark" : "text-muted"}`}
                    >
                      {currentTypeObj?.name || "Select Type"}
                    </span>
                    {currentTypeObj?.type && <TimeOffTypeBadge type={currentTypeObj.type} />}
                  </div>
                  <RightOutlined className="text-secondary drawer-arrow-icon" />
                </div>
                {errors.type && (
                  <div className="text-danger mt-n2 mb-2 px-1 form-item-error">
                    {errors.type}
                  </div>
                )}

                {/* Card 4: From & To row card */}
                <div
                  className="bg-white rounded-3 mb-3 overflow-hidden shadow-sm drawer-card"
                >
                  {/* From row */}
                  <div
                    className="d-flex align-items-center justify-content-between px-3 py-3 border-bottom drawer-row-item"
                  >
                    <span className="drawer-row-label">
                      From
                    </span>
                    <DatePicker
                      value={startDate}
                      onChange={(date) => {
                        setStartDate(date);
                        if (isTimeSlot) {
                          setEndDate(date);
                        } else if (endDate && date && endDate.isBefore(date, "day")) {
                          setEndDate(null);
                        }
                        setErrors((prev: any) => ({
                          ...prev,
                          startDate: undefined,
                          endDate: isTimeSlot ? undefined : prev.endDate,
                        }));
                        const targetEndDate = isTimeSlot ? date : endDate;
                        if (date && targetEndDate && date.isSame(targetEndDate, "day")) {
                          if (typeof startTimeType === "string") {
                            if (typeof endTimeType !== "string" || endTimeType <= startTimeType) {
                              const endSlots = getSlotsForDate(targetEndDate);
                              const nextSlot = endSlots.find((t) => t > startTimeType) || endSlots[endSlots.length - 1];
                              setEndTimeType(nextSlot);
                            }
                          } else if (isAfternoonStart(startTimeType) && isLunchtimeEnd(endTimeType)) {
                            setEndTimeType(4);
                          }
                        }
                      }}
                      format="ddd, D MMM"
                      placeholder="Select Date"
                      bordered={false}
                      suffixIcon={null}
                      allowClear={false}
                      disabledDate={disabledStartDate}
                      className="me-auto drawer-datepicker-input"
                      inputReadOnly
                    />
                    <span
                      onClick={() => {
                        setDrawerType("from");
                        setTimeDrawerOpen(true);
                      }}
                      className="drawer-time-btn"
                    >
                      {startTimeType === 1 ? "Morning" : startTimeType === 2 ? "Afternoon" : formatTimeOffSlot(startTimeType)}
                    </span>
                  </div>

                  {/* To row */}
                  <div
                    className="d-flex align-items-center justify-content-between px-3 py-3 drawer-row-item"
                  >
                    <span className="drawer-row-label">
                      To
                    </span>
                    <DatePicker
                      value={endDate}
                      defaultPickerValue={endDate || startDate || undefined}
                      onChange={(date) => {
                        if (isTimeSlot && startDate) {
                          setEndDate(startDate);
                          return;
                        }
                        setEndDate(date);
                        if (date && startDate && date.isSame(startDate, "day")) {
                          if (typeof startTimeType === "string") {
                            if (typeof endTimeType !== "string" || endTimeType <= startTimeType) {
                              const endSlots = getSlotsForDate(date);
                              const nextSlot = endSlots.find((t) => t > startTimeType) || endSlots[endSlots.length - 1];
                              setEndTimeType(nextSlot);
                            }
                          } else if (isAfternoonStart(startTimeType) && isLunchtimeEnd(endTimeType)) {
                            setEndTimeType(4);
                          }
                        }
                      }}
                      format="ddd, D MMM"
                      placeholder="Select Date"
                      bordered={false}
                      suffixIcon={null}
                      allowClear={false}
                      disabledDate={(current) => {
                        if (isTimeSlot && startDate) {
                          return !current.isSame(startDate, "day");
                        }
                        return disabledEndDate(current);
                      }}
                      className={`me-auto ${isTimeSlot && startDate ? "drawer-datepicker-readonly" : "drawer-datepicker-input"}`}
                      inputReadOnly
                    />
                    <span
                      onClick={() => {
                        setDrawerType("to");
                        setTimeDrawerOpen(true);
                      }}
                      className="drawer-time-btn"
                    >
                      {endTimeType === 3 ? "Lunchtime" : endTimeType === 4 ? "End of Day" : formatTimeOffSlot(endTimeType)}
                    </span>
                  </div>
                </div>
                {errors.startDate && (
                  <div className="text-danger mt-n2 mb-2 px-1 form-item-error">
                    {errors.startDate}
                  </div>
                )}
                {errors.endDate && (
                  <div className="text-danger mt-n2 mb-2 px-1 form-item-error">
                    {errors.endDate}
                  </div>
                )}

                {/* Card 3: Reason */}
                <div
                  className="bg-white rounded-3 p-3 mb-3 d-flex align-items-center gap-2 gap-md-3 shadow-sm drawer-card"
                >
                  <div className="drawer-reason-icon">
                    <MessageFilled className="drawer-arrow-icon" />
                  </div>
                  <input
                    type="text"
                    className="form-control border-0 p-0 shadow-none bg-transparent drawer-reason-input"
                    placeholder="Add a reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>

                {/* Upload Attachments */}
                <div className="bg-white rounded-3 p-3 mb-3 shadow-sm drawer-card">
                  <div className="custom-label mb-2 fw-semibold drawer-form-label">
                    Upload Attachments
                  </div>
                  <S3FileUploader
                    folder="time_off_request_attachments"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp,.svg,.mp4,.mov,.avi,.mkv,.zip,.rar"
                    multiple={true}
                    maxSize={500 * 1024 * 1024}
                    onUploadStart={() => setAttachmentsUploadingCount((prev) => prev + 1)}
                    onSuccess={handleUploadSuccess}
                    onError={() => setAttachmentsUploadingCount((prev) => Math.max(0, prev - 1))}
                    className="bg-white rounded-3 py-0 drawer-uploader-drop-box"
                  >
                    {attachmentsUploadingCount > 0 ? (
                      <div className="py-2 text-center">
                        <Spin tip="Uploading Attachment..." />
                      </div>
                    ) : (
                      <>
                        <p className="ant-upload-drag-icon text-center mb-2 d-flex justify-content-center">
                          <span className="svg-48">{(svg as any)?.importIcon || "📄"}</span>
                        </p>
                        <p className="ant-upload-text fw-semibold text-center fs-6 text-secondary">
                          Drag & Drop file to upload /{" "}
                          <span className="text-primary text-decoration-underline cursor-pointer">
                            CLICK HERE
                          </span>
                        </p>
                      </>
                    )}
                  </S3FileUploader>

                  {modalStagedFiles.length > 0 && (
                    <div className="mt-3 d-flex flex-column gap-2">
                      {modalStagedFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="d-flex align-items-center justify-content-between p-2 rounded-2 border bg-light"
                        >
                          <div className="d-flex align-items-center gap-2 text-truncate me-2 flex-grow-1 overflow-hidden">
                            {getFileIcon(file.attachment)}
                            <span className="text-truncate drawer-file-item-name">
                              {getFileNameFromUrl(file.attachment)}
                            </span>
                          </div>
                          <Button
                            type="text"
                            danger
                            icon={(svg as any)?.deleteBtn || <DeleteOutlined />}
                            size="small"
                            className="p-0 border-0"
                            onClick={() => handleRemoveFile(idx)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Deduction display */}
                {canShowSlots && typeof startTimeType === "string" && typeof endTimeType === "string" && slotDurationHHMM ? (
                  <div className="text-center my-3 drawer-allowance-text">
                    <span>
                      Takes <span className="text-danger fw-semibold px-1">{slotDurationHHMM}</span> {slotDurationHHMM === "01:00" ? "hour" : "hours"}
                    </span>
                  </div>
                ) : isDeductible && allowanceDays !== null && currentEmployee ? (
                  <div className="text-center my-3 drawer-allowance-text">
                    {isPreviousYear && availableAllowance <= 0 ? (
                      <span className="text-danger fw-semibold">
                        No time off allowance available for {selectedDateYear} (0 Available)
                      </span>
                    ) : (
                      <span>
                        Takes <span className="text-danger fw-semibold px-1">{formatAllowanceDays(allowanceDays)}</span> {allowanceDays === 1 || allowanceDays === 0.5 ? "day" : "days"} from {isFutureYear ? `${selectedDateYear} ` : ""}allowance ({formatAllowanceDays(availableAllowance)} Available)
                      </span>
                    )}
                  </div>
                ) : null}

                {/* Allowance warning if exceeded */}
                {isDeductible && allowanceDays !== null && currentEmployee && allowanceDays > availableAllowance && (
                  <div
                    className="mb-3 p-3 w-100 rounded-3 drawer-allowance-warning-box"
                  >
                    <div className="fw-bold mb-1 drawer-allowance-warning-title">
                      Time Off Balance Warning
                    </div>
                    <div className="drawer-allowance-warning-content">
                      {employeeId ? (
                        <>You have <strong>{formatAllowanceDays(availableAllowance)} {availableAllowance === 1 || availableAllowance === 0.5 ? "day" : "days"}</strong> of {isFutureYear ? `${selectedDateYear} ` : ""}time off remaining, but you are requesting <strong>{formatAllowanceDays(allowanceDays)} {allowanceDays === 1 || allowanceDays === 0.5 ? "day" : "days"}</strong>. The first {formatAllowanceDays(availableAllowance)} {availableAllowance === 1 || availableAllowance === 0.5 ? "day" : "days"} will be approved, and the remaining {formatAllowanceDays(allowanceDays - availableAllowance)} {allowanceDays - availableAllowance === 1 || allowanceDays - availableAllowance === 0.5 ? "day" : "days"} will be rejected.</>
                      ) : (
                        <>This employee has only <strong>{formatAllowanceDays(availableAllowance)} {availableAllowance === 1 || availableAllowance === 0.5 ? "day" : "days"}</strong> of {isFutureYear ? `${selectedDateYear} ` : ""}time off remaining but has requested <strong>{formatAllowanceDays(allowanceDays)} {allowanceDays === 1 || allowanceDays === 0.5 ? "day" : "days"}</strong>. If you approve this request, {formatAllowanceDays(availableAllowance)} {availableAllowance === 1 || availableAllowance === 0.5 ? "day" : "days"} will be approved and the remaining {formatAllowanceDays(allowanceDays - availableAllowance)} {allowanceDays - availableAllowance === 1 || allowanceDays - availableAllowance === 0.5 ? "day" : "days"} will be rejected.</>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Bottom action buttons */}
              <div
                className="d-flex justify-content-center gap-3 mt-auto pt-3 drawer-action-bar"
              >
                <Button
                  className="fw-bold rounded-1 px-4 px-md-5 btn-close-modal"
                  size="large"
                  onClick={handleClose}
                >
                  Close
                </Button>
                <Button
                  type="primary"
                  className="fw-bold rounded-1 px-4 px-md-5 border-0 btn-submit-modal"
                  size="large"
                  loading={loading}
                  onClick={handleSubmit}
                >
                  {editData ? "UPDATE" : "CREATE"}
                </Button>
              </div>
            </div>
          </Drawer>

          {/* Sub-Drawer 1: Time Picker (From & To) */}
          <Drawer
            placement="bottom"
            open={timeDrawerOpen}
            onClose={() => setTimeDrawerOpen(false)}
            height="90vh"
            zIndex={1200}
            closable={false}
            className="time-picker-bottom-drawer"
            styles={{
              body: {
                padding: 0,
                backgroundColor: "#f5f6f8",
                display: "flex",
                flexDirection: "column",
              },
              wrapper: {
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                overflow: "hidden",
              },
              content: {
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                overflow: "hidden",
              },
            }}
          >
            <div
              className="d-flex align-items-center position-relative py-3 px-3 bg-white border-bottom flex-shrink-0 drawer-header-bar"
            >
              <button
                type="button"
                className="btn p-0 border-0 bg-transparent d-flex align-items-center justify-content-center drawer-back-btn svg-24"
                onClick={() => setTimeDrawerOpen(false)}
                aria-label="Back"
              >
                {svg?.backArrow || "←"}
              </button>
              <div
                className="position-absolute start-0 end-0 text-center fw-bold drawer-title"
              >
                {drawerType === "from" ? "From" : "To"}
              </div>
            </div>

            <div
              className="p-3 drawer-body-scroll"
            >
              <div
                className="bg-white rounded-3 overflow-hidden shadow-sm subdrawer-card"
              >
                {(drawerType === "from" ? currentFromOptions : currentToOptions).length === 0 ? (
                  <div className="p-4 text-center text-secondary">
                    No available {drawerType === "from" ? "start times" : "end times"}
                  </div>
                ) : (
                  (drawerType === "from" ? currentFromOptions : currentToOptions).map((opt, idx, arr) => {
                    const isSelected =
                      drawerType === "from"
                        ? startTimeType === opt.value
                        : endTimeType === opt.value;
                    const isLast = idx === arr.length - 1;

                    return (
                      <div
                        key={String(opt.value)}
                        className={`d-flex align-items-center justify-content-between px-3 py-3 subdrawer-item ${!isLast ? "border-bottom" : ""} ${isSelected ? "selected" : ""}`}
                        onClick={() => {
                          if (drawerType === "from") {
                            const selectedVal = opt.value;
                            setStartTimeType(selectedVal);
                            if (typeof selectedVal === "string") {
                              if (startDate) {
                                setEndDate(startDate);
                              } else if (endDate) {
                                setStartDate(endDate);
                              }
                              const effectiveDate = startDate || endDate || dayjs();
                              const endSlots = getSlotsForDate(effectiveDate);
                              if (typeof endTimeType !== "string" || endTimeType <= selectedVal) {
                                const nextSlot = endSlots.find((t) => t > selectedVal) || endSlots[endSlots.length - 1];
                                setEndTimeType(nextSlot);
                              }
                            } else {
                              if (typeof endTimeType === "string") {
                                setEndTimeType(4);
                              } else if (selectedVal === 2) {
                                setEndTimeType(4);
                              } else if (
                                isAfternoonStart(selectedVal) &&
                                isLunchtimeEnd(endTimeType) &&
                                (!startDate || !endDate || startDate.isSame(endDate, "day"))
                              ) {
                                setEndTimeType(4);
                              }
                            }
                          } else {
                            setEndTimeType(opt.value);
                            if (typeof opt.value === "string") {
                              if (startDate) {
                                setEndDate(startDate);
                              } else if (endDate) {
                                setStartDate(endDate);
                              }
                            }
                          }
                          setTimeDrawerOpen(false);
                        }}
                      >
                        <span className={isSelected ? "fw-medium" : ""}>{opt.label}</span>
                        {isSelected && (
                          <CheckOutlined
                            className="text-success fs-6"
                          />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </Drawer>

          {/* Sub-Drawer 2: Time Off Type Drawer */}
          <Drawer
            placement="bottom"
            open={typeDrawerOpen}
            onClose={() => setTypeDrawerOpen(false)}
            height="80vh"
            zIndex={1200}
            closable={false}
            className="time-picker-bottom-drawer"
            styles={{
              body: {
                padding: 0,
                backgroundColor: "#f5f6f8",
                display: "flex",
                flexDirection: "column",
              },
              wrapper: {
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                overflow: "hidden",
              },
              content: {
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                overflow: "hidden",
              },
            }}
          >
            <div
              className="d-flex align-items-center position-relative py-3 px-3 bg-white border-bottom flex-shrink-0 drawer-header-bar"
            >
              <button
                type="button"
                className="btn p-0 border-0 bg-transparent d-flex align-items-center justify-content-center drawer-back-btn svg-24"
                onClick={() => setTypeDrawerOpen(false)}
                aria-label="Back"
              >
                {svg?.backArrow || "←"}
              </button>
              <div
                className="position-absolute start-0 end-0 text-center fw-bold drawer-title"
              >
                Type
              </div>
            </div>

            <div
              className="p-3 drawer-body-scroll"
            >
              <div
                className="bg-white rounded-3 overflow-hidden shadow-sm subdrawer-card"
              >
                {typesLoading ? (
                  <div className="p-4 text-center">
                    <Spin tip="Loading Types..." />
                  </div>
                ) : availableTimeOffTypes.length === 0 ? (
                  <div className="p-4 text-center text-secondary">No time off types available</div>
                ) : (
                  availableTimeOffTypes.map((type: any, idx: number) => {
                    const isSelected = selectedType === type._id;
                    const isLast = idx === timeOffTypes.length - 1;

                    return (
                      <div
                        key={type._id}
                        className={`d-flex align-items-center justify-content-between px-3 py-3 subdrawer-item ${!isLast ? "border-bottom" : ""} ${isSelected ? "selected" : ""}`}
                        onClick={() => {
                          setSelectedType(type._id);
                          setTypeDrawerOpen(false);
                        }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="subdrawer-type-box"
                            style={{
                              backgroundColor: type.color || (type.type === "unpaid" ? "#16a34a" : "#10b981"),
                            }}
                          />
                          <span>{type.name}</span>
                          {type.type && <TimeOffTypeBadge type={type.type} />}
                        </div>
                        {isSelected && (
                          <CheckOutlined
                            className="text-success fs-6"
                          />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </Drawer>

          {/* Sub-Drawer 3: Employee Picker Drawer (for Admins when !employeeId) */}
          {!employeeId && (
            <Drawer
              placement="bottom"
              open={employeeDrawerOpen}
              onClose={() => setEmployeeDrawerOpen(false)}
              height="80vh"
              zIndex={1200}
              closable={false}
              className="time-picker-bottom-drawer"
              styles={{
                body: {
                  padding: 0,
                  backgroundColor: "#f5f6f8",
                  display: "flex",
                  flexDirection: "column",
                },
                wrapper: {
                  borderTopLeftRadius: "16px",
                  borderTopRightRadius: "16px",
                  overflow: "hidden",
                },
                content: {
                  borderTopLeftRadius: "16px",
                  borderTopRightRadius: "16px",
                  overflow: "hidden",
                },
              }}
            >
              <div
                className="d-flex align-items-center position-relative py-3 px-3 bg-white border-bottom flex-shrink-0 drawer-header-bar"
              >
                <button
                  type="button"
                  className="btn p-0 border-0 bg-transparent d-flex align-items-center justify-content-center drawer-back-btn svg-24"
                  onClick={() => setEmployeeDrawerOpen(false)}
                  aria-label="Back"
                >
                  {svg?.backArrow || "←"}
                </button>
                <div
                  className="position-absolute start-0 end-0 text-center fw-bold drawer-title"
                >
                  Who for
                </div>
              </div>

              <div className="p-3 border-bottom bg-white flex-shrink-0">
                <Input
                  prefix={<SearchOutlined className="text-secondary" />}
                  placeholder="Search employee..."
                  allowClear
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  className="rounded-3"
                />
              </div>

              <div
                className="p-3 drawer-body-scroll"
              >
                <div
                  className="bg-white rounded-3 overflow-hidden shadow-sm subdrawer-card"
                >
                  {filteredTeamMembers.length === 0 ? (
                    <div className="p-4 text-center text-secondary">No employees found</div>
                  ) : (
                    filteredTeamMembers.map((emp: any, idx: number) => {
                      const empName = emp.name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim();
                      const isSelected = selectedEmployee === emp._id;
                      const isLast = idx === filteredTeamMembers.length - 1;

                      return (
                        <div
                          key={emp._id}
                          className={`d-flex align-items-center justify-content-between px-3 py-3 subdrawer-item ${!isLast ? "border-bottom" : ""} ${isSelected ? "selected" : ""}`}
                          onClick={() => {
                            setSelectedEmployee(emp._id);
                            setEmployeeDrawerOpen(false);
                          }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <EmployeeAvatar
                              employee={emp}
                              name={empName}
                              className="subdrawer-avatar"
                            />
                            <span className="fw-medium">{empName}</span>
                          </div>
                          {isSelected && (
                            <CheckOutlined
                              className="text-success fs-6"
                            />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </Drawer>
          )}
        </>
      )}
      {alreadyAppliedNotice?.visible && (
        <AlreadyAppliedTimeOffModal
          visible={alreadyAppliedNotice.visible}
          onClose={handleCloseNotice}
          alreadyAppliedLeaves={alreadyAppliedNotice.leaves}
          createdCount={alreadyAppliedNotice.createdCount}
          createdDateRange={alreadyAppliedNotice.createdDateRange}
          createdRequests={alreadyAppliedNotice.lastData}
          employeeName={alreadyAppliedNotice.employeeName}
        />
      )}
    </>
  );
};

